import React, { useState } from 'react';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { MappedProfileType, TikTokProfileValues } from '../SocialProfileListItem/SocialProfileListItem.view';
import { SocialProfile } from '../SocialProfile';
import { profilesHelpers } from '../../../modules/Settings/Profiles/Profiles.helpers';
import { Link } from 'react-router-dom';
import { CheckboxList } from '../../forms/CheckboxList';
import { SelectAllValidationType } from '../../actions/SelectAllCheckboxes/SelectAllCheckboxes.view';
import { SocialProfileFieldsFragment } from '../../../models/graphql';
import { SocialProfileSelectorListItem } from '../SocialProfileSelectorListItem';

export type SocialProfileSelectorListWithPinterestBoard = {
  pinterestFormName: string;
  pinterestRules: RegisterOptions;
};

export type SocialProfileSelectorListWithoutPinterestBoard = {
  pinterestFormName?: never;
  pinterestRules?: never;
};

export type SocialProfileSelectorWithTikTokSettings = {
  tikTokFormName: string;
  tikTokTargetRules: RegisterOptions;
  tikTokActionRules: RegisterOptions;
  tikTokPromotionRules: RegisterOptions;
};

export type SocialProfileSelectorWithoutTikTokSettings = {
  tikTokFormName?: never;
  tikTokTargetRules?: never;
  tikTokActionRules?: never;
  tikTokPromotionRules?: never;
};

export type SocialProfileSelectorListViewProps = {
  //
  useFormInstance: UseFormReturn;
  formFieldName: string;
  profiles: MappedProfileType[];
  selectedProfiles?: SelectedSocialProfileValue[];
  disabledProfileIds: string[];
  selectAllValidation?: SelectAllValidationType;
  selectAllFormName?: string;
  dropdownRef?: any;
  dropdownExpanded?: boolean;
} & (SocialProfileSelectorListWithPinterestBoard | SocialProfileSelectorListWithoutPinterestBoard) &
  (SocialProfileSelectorWithTikTokSettings | SocialProfileSelectorWithoutTikTokSettings);

export type SelectedSocialProfileValue = Readonly<{
  __typename: MappedProfileType['__typename'] | undefined;
  profileId: string;
  pnBoardId?: string;
  tikTok?: TikTokProfileValues;
}>;

export type TikTokComponentPropValues = {
  tikTokTargetValue: TikTokProfileValues['privacyTarget'];
  tikTokActionValue: TikTokProfileValues['allowedActions'];
  tikTokPromotionValue: TikTokProfileValues['promotion'];
};

export const SocialProfileSelectorListView = (props: SocialProfileSelectorListViewProps): JSX.Element => {
  const {
    selectedProfiles,
    useFormInstance,
    selectAllFormName,
    disabledProfileIds,
    profiles,
    pinterestFormName,
    pinterestRules,
    tikTokFormName,
    tikTokTargetRules,
    tikTokActionRules,
    tikTokPromotionRules,
    formFieldName,
    selectAllValidation,
    dropdownRef,
    dropdownExpanded
  } = props;
  const [callBackToRunOnWrapperClick, setCallBackToRunOnWrapperClick] = useState<{
    [key: string]: (checked: boolean) => void;
  }>({});
  const isSelectedProfile = (profile: MappedProfileType): boolean =>
    !!selectedProfiles?.map((elem: SelectedSocialProfileValue) => elem.profileId).includes(profile.id);

  const pnSelectedBoard = (profile: MappedProfileType): string | null => {
    const index = selectedProfiles?.findIndex(elem => elem.profileId === profile.id && elem.pnBoardId);
    if (index !== undefined && index > -1) {
      return selectedProfiles?.[index].pnBoardId || null;
    }
    return null;
  };

  const tiktokValues = (profile: MappedProfileType): TikTokComponentPropValues | null => {
    const index = selectedProfiles?.findIndex(elem => elem.profileId === profile.id && elem.tikTok);
    if (index !== undefined && index > -1) {
      const { tikTok } = selectedProfiles?.[index] || { tikTok: undefined };
      if (tikTok) {
        const {
          allowedActions: tikTokActionValue,
          promotion: tikTokPromotionValue,
          privacyTarget: tikTokTargetValue
        } = tikTok;
        return {
          tikTokTargetValue,
          tikTokActionValue,
          tikTokPromotionValue
        };
      } else {
        return {
          tikTokTargetValue: null,
          tikTokActionValue: [],
          tikTokPromotionValue: []
        };
      }
    }
    return null;
  };

  return (
    <CheckboxList
      selectAll
      checkboxesChildrenGroupName={formFieldName}
      selectAllName={selectAllFormName || 'select_all_profiles'}
      selectAllSize={'small'}
      {...(selectAllValidation ? { selectAllValidation: selectAllValidation } : {})}
      borderless
      wrapperClickable
      formInstance={useFormInstance}
      stopPropagation={true}
      label={''}>
      {profiles.map((profile, index) => {
        const formName = `${formFieldName}.${index}`;
        return (
          <SocialProfileSelectorListItem
            disabled={props.disabledProfileIds.includes(profile.id)}
            key={`${profile.id}`}
            useFormInstance={props.useFormInstance}
            useFormName={formName}
            useFormValue={profile.id}
            callbackToOpenSettingsDropdown={callBackToRunOnWrapperClick[profile.id]}
            socialProfileComponent={
              <SocialProfile
                socialProfile={profilesHelpers.remapSocialProfile(profile, false, true, true)}
                size={'small'}
                isParentDropdownExpanded={dropdownExpanded}
                checkbox
                useFormInstance={useFormInstance}
                label
                sublabel={false}
                dropdownRef={dropdownRef}
                setCallbackOnWrapperClick={setCallBackToRunOnWrapperClick}
                {...(profile.__typename !== 'Bitly' && (profile as SocialProfileFieldsFragment).photoUrl
                  ? { image: (profile as SocialProfileFieldsFragment).photoUrl }
                  : {})}
                useFormName={formName}
                {...(pinterestFormName && profile.__typename === 'PinterestSocialProfile'
                  ? {
                      pinterestBoard: true,
                      pinterestFormName: `${pinterestFormName}.${index}`,
                      // we use `index` to map for main profile here. We need to make sure we compare against main profile form name values on submit, in order to properly map pn boards to selected profiles
                      pinterestRules,
                      ...(!!pnSelectedBoard(profile) ? { pinterestBoardValue: pnSelectedBoard(profile) as string } : {})
                    }
                  : { pinterestBoard: false })}
                {...(tikTokFormName && profile.__typename === 'TikTokSocialProfile'
                  ? {
                      tikTokSettings: true,
                      tikTokFormName: `${tikTokFormName}.${index}`,
                      // we use `index` to map for main profile here. We need to make sure we compare against main profile form name values on submit, in order to properly map tiktok values to selected profiles
                      tikTokTargetRules,
                      tikTokActionRules,
                      tikTokPromotionRules,
                      ...(!!tiktokValues(profile) ? { ...tiktokValues(profile) } : {})
                    }
                  : { tikTokSettings: false })}
                rules={{}}
                disabled={disabledProfileIds.includes(profile.id)}
                value={profile.id}
                disconnected={!profile.connected}
                {...(isSelectedProfile(profile) ? { checked: true } : {})}
              />
            }
          />
        );
      })}
      {profiles.length < 1 ? (
        <>
          No Profiles connected.
          <br className={'show-for-medium'} />
          <Link to={'/settings/profiles'} target={'_blank'}>
            Connect a Profile
          </Link>{' '}
          to add Posts
        </>
      ) : null}
    </CheckboxList>
  );
};
